/**
 * Bulk changes to fans run one OnlyFans call per fan (OnlyFans has no batch
 * endpoint for list membership). This paces them so one account never floods
 * OnlyFans, and reports each fan's outcome so the page can show it.
 */

export interface BulkJobResult {
  /** Ids that succeeded, in the order they were given. */
  done: string[]
  failed: { id: string; error: string }[]
  cancelled: boolean
}

export type BulkItemState =
  | { status: 'running'; error?: undefined }
  | { status: 'done'; error?: undefined }
  | { status: 'failed'; error: string }

export interface BulkJobOptions {
  items: string[]
  run: (id: string) => Promise<void>
  /** OnlyFans calls in flight at once. */
  concurrency?: number
  /** Called as each fan starts and finishes. */
  onUpdate?: (id: string, state: BulkItemState) => void
  /** Injected for tests; defaults to a real timer. */
  wait?: (ms: number) => Promise<void>
}

export interface BulkJob {
  result: Promise<BulkJobResult>
  isSettled: () => boolean
  /** Start no new calls; the ones in flight still finish and are reported. */
  cancel: () => void
}

/** Pauses after consecutive rate limits; a fan still limited after the last one fails. */
const RATE_LIMIT_BACKOFF_MS = [5000, 15000, 60000]

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

function isRateLimited(e: unknown): boolean {
  return (e as { status?: unknown } | null)?.status === 429
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export function runBulkJob({
  items,
  run,
  concurrency = 2,
  onUpdate,
  wait = sleep,
}: BulkJobOptions): BulkJob {
  const outcome = new Map<string, string | null>()
  let next = 0
  let settled = false
  let cancelled = false
  // While OnlyFans rate-limits us, every lane waits on the same pause.
  let pause: Promise<void> | null = null
  let limitedInARow = 0

  const backOff = () => {
    if (!pause) {
      const ms = RATE_LIMIT_BACKOFF_MS[limitedInARow - 1]!
      pause = wait(ms).then(() => {
        pause = null
      })
    }
    return pause
  }

  /**
   * One fan, retried through rate limits: null when done, the error text when
   * failed, undefined when cancelled before it could run again.
   */
  const attempt = async (id: string): Promise<string | null | undefined> => {
    for (;;) {
      if (pause) await pause
      try {
        await run(id)
        limitedInARow = 0
        return null
      } catch (e) {
        if (!isRateLimited(e)) return errorText(e)
        limitedInARow += 1
        if (limitedInARow > RATE_LIMIT_BACKOFF_MS.length) return 'Rate limited'
        await backOff()
        if (cancelled) return undefined
      }
    }
  }

  const worker = async () => {
    while (!cancelled && next < items.length) {
      const id = items[next++]!
      onUpdate?.(id, { status: 'running' })
      const result = await attempt(id)
      if (result === undefined) continue
      outcome.set(id, result)
      onUpdate?.(id, result === null ? { status: 'done' } : { status: 'failed', error: result })
    }
  }

  const result = Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker)).then(
    (): BulkJobResult => {
      settled = true
      return {
        done: items.filter((id) => outcome.get(id) === null),
        failed: items.flatMap((id) => {
          const error = outcome.get(id)
          return typeof error === 'string' ? [{ id, error }] : []
        }),
        cancelled,
      }
    },
  )

  return {
    result,
    isSettled: () => settled,
    cancel: () => {
      cancelled = true
    },
  }
}
